import React from 'react';

import { useApp } from '#states/hooks.js';

import { AppImage } from './AppImage.js';
import Loader from './loader.js';

interface Slide {
  src: { jpeg: string; webp?: string; avif?: string };
  location: string;
  photographer: string;
  unsplashId: string;
}

const SLIDE_CONFIGS: Slide[] = [
  {
    src: {
      jpeg: 'bjorn-snelders-PeLkhi_B3wI-unsplash.jpg',
      webp: 'bjorn-snelders-PeLkhi_B3wI-unsplash.webp',
      avif: 'bjorn-snelders-PeLkhi_B3wI-unsplash.avif',
    },
    location: 'Cinque Terre, Manarola, Italy',
    photographer: 'Bjorn Snelders',
    unsplashId: 'PeLkhi_B3wI',
  },
  {
    src: {
      jpeg: 'alfiano-sutianto-exFdOWkYBQw-unsplash.jpg',
      webp: 'alfiano-sutianto-exFdOWkYBQw-unsplash.webp',
      avif: 'alfiano-sutianto-exFdOWkYBQw-unsplash.avif',
    },
    location: 'Bali, Indonesia',
    photographer: 'Alfiano Sutianto',
    unsplashId: 'exFdOWkYBQw',
  },
  {
    src: {
      jpeg: 'tianshu-liu-aqZ3UAjs_M4-unsplash.jpg',
      webp: 'tianshu-liu-aqZ3UAjs_M4-unsplash.webp',
      avif: 'tianshu-liu-aqZ3UAjs_M4-unsplash.avif',
    },
    location: 'Chureito Pagoda, Fujiyoshida, Japan',
    photographer: 'Tianshu Liu',
    unsplashId: 'aqZ3UAjs_M4',
  },
  {
    src: {
      jpeg: 'mike-enerio-4HVCsDOg0qI-unsplash.jpg',
      webp: 'mike-enerio-4HVCsDOg0qI-unsplash.webp',
      avif: 'mike-enerio-4HVCsDOg0qI-unsplash.avif',
    },
    location: 'Singapore',
    photographer: 'Mike Enerio',
    unsplashId: '4HVCsDOg0qI',
  },
  {
    src: {
      jpeg: 'pietro-de-grandi-T7K4aEPoGGk-unsplash.jpg',
      webp: 'pietro-de-grandi-T7K4aEPoGGk-unsplash.webp',
      avif: 'pietro-de-grandi-T7K4aEPoGGk-unsplash.avif',
    },
    location: 'Pragser Wildsee, Italy',
    photographer: 'Pietro De Grandi',
    unsplashId: 'T7K4aEPoGGk',
  },
  {
    src: {
      jpeg: 'rachel-davis-tn2rBnvIl9I-unsplash.jpg',
      webp: 'rachel-davis-tn2rBnvIl9I-unsplash.webp',
      avif: 'rachel-davis-tn2rBnvIl9I-unsplash.avif',
    },
    location: 'Neuschwanstein Castle, Germany',
    photographer: 'Rachel Davis',
    unsplashId: 'tn2rBnvIl9I',
  },
];

// Randomize slide order on each page load
const RANDOMIZED_SLIDES = [...SLIDE_CONFIGS].toSorted(() => Math.random() - 0.5);

export function LoginSlideshow() {
  const SLIDE_DURATION_MS = 10_000; // matches setInterval
  const PREFETCH_LEAD_MS = 2000; // mount next ~2s before transition

  // Get Firebase Storage services from the DI container
  const { firebase } = useApp('firebase');

  const [index, setIndex] = React.useState<number>(0);
  const [slides, setSlides] = React.useState<Slide[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [loadingSlides, setLoadingSlides] = React.useState<Set<number>>(new Set());

  const currentSlide: Slide | undefined = slides[index];
  const nextIndex = (index + 1) % RANDOMIZED_SLIDES.length;

  // Check browser support for different formats (cached)
  const supportsWebP = React.useMemo(() => {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    return canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
  }, []);

  const supportsAVIF = React.useMemo(() => {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    return canvas.toDataURL('image/avif').indexOf('data:image/avif') === 0;
  }, []);

  // Consolidated slide loading function
  const loadSlideUrls = React.useCallback(
    async (slideConfig: Slide): Promise<Slide> => {
      // Always load JPEG as fallback
      const jpegPathRef = firebase.storage.ref(`login-images/${slideConfig.src.jpeg}`);
      const jpegUrl = await firebase.storage.getDownloadURL(jpegPathRef);

      const src: { jpeg: string; webp?: string; avif?: string } = { jpeg: jpegUrl };

      // Only load WebP if browser supports it and file exists
      if (slideConfig.src.webp && supportsWebP) {
        const webpPathRef = firebase.storage.ref(`login-images/${slideConfig.src.webp}`);
        src.webp = await firebase.storage.getDownloadURL(webpPathRef);
      }

      // Only load AVIF if browser supports it and file exists
      if (slideConfig.src.avif && supportsAVIF) {
        const avifPathRef = firebase.storage.ref(`login-images/${slideConfig.src.avif}`);
        src.avif = await firebase.storage.getDownloadURL(avifPathRef);
      }

      return {
        src,
        location: slideConfig.location,
        photographer: slideConfig.photographer,
        unsplashId: slideConfig.unsplashId,
      };
    },
    [firebase, supportsWebP, supportsAVIF],
  );

  // Load slides as needed
  React.useEffect(() => {
    async function loadSlideIfNeeded(slideIndex: number) {
      // Skip if already loaded or currently loading
      if (slides[slideIndex] || loadingSlides.has(slideIndex)) {
        return;
      }

      const slideConfig = RANDOMIZED_SLIDES[slideIndex];
      if (!slideConfig) {
        return;
      }

      try {
        setLoadingSlides((prev) => new Set(prev).add(slideIndex));
        const loadedSlide = await loadSlideUrls(slideConfig);

        setSlides((prev) => {
          const newSlides = [...prev];
          newSlides[slideIndex] = loadedSlide;
          return newSlides;
        });
        setLoadingSlides((prev) => {
          const newSet = new Set(prev);
          newSet.delete(slideIndex);
          return newSet;
        });
      } catch (error) {
        console.error(`Failed to load slide ${slideIndex}:`, error);
      }
    }

    // Load initial slide immediately
    if (slides.length === 0) {
      void loadSlideIfNeeded(0);
      setIsLoading(false);
    }

    // Preload next slide
    void loadSlideIfNeeded(nextIndex);
  }, [index, nextIndex, slides, loadingSlides, loadSlideUrls]);

  // Track which slides are currently mounted. Start with current only.
  const [mounted, setMounted] = React.useState<Set<number>>(() => new Set([index]));

  // Keep a ref to the prefetch timeout so we can cancel on index change/unmount
  const prefetchTimeoutRef = React.useRef<number | undefined>(undefined);

  React.useEffect(() => {
    if (!slides[0]) return;

    const interval = setInterval(() => {
      setIndex((i) => (i + 1) % RANDOMIZED_SLIDES.length);
    }, SLIDE_DURATION_MS);
    return () => clearInterval(interval);
  }, [slides]);

  // On index change, ensure current is mounted and schedule prefetch for next just before transition
  React.useEffect(() => {
    if (!slides[0]) return;

    // Always ensure current is mounted
    setMounted((prev) => {
      if (prev.has(index)) return prev;
      const next = new Set(prev);
      next.add(index);
      return next;
    });

    // Clear any prior prefetch timer
    if (prefetchTimeoutRef.current !== undefined) {
      window.clearTimeout(prefetchTimeoutRef.current);
      prefetchTimeoutRef.current = undefined;
    }

    // Schedule mounting of next slide shortly before transition
    const delay = Math.max(0, SLIDE_DURATION_MS - PREFETCH_LEAD_MS);
    prefetchTimeoutRef.current = window.setTimeout(() => {
      setMounted((prev) => {
        if (prev.has(nextIndex)) return prev;
        const next = new Set(prev);
        next.add(nextIndex);
        return next;
      });
    }, delay);

    return () => {
      if (prefetchTimeoutRef.current !== undefined) {
        window.clearTimeout(prefetchTimeoutRef.current);
        prefetchTimeoutRef.current = undefined;
      }
    };
  }, [index, nextIndex, slides]);

  const handleTransitionEnd = (i: number, e: React.TransitionEvent<HTMLPictureElement>) => {
    if (e.propertyName !== 'opacity') return;
    const isCurrent = i === index;
    const isNext = i === nextIndex;
    if (isCurrent || isNext) return;
    // Safe to unmount this slide after fade-out completed
    setMounted((prev) => {
      if (!prev.has(i)) return prev;
      const next = new Set(prev);
      next.delete(i);
      return next;
    });
  };

  if (isLoading || !currentSlide) {
    return (
      <div className="relative w-full h-full overflow-hidden bg-background-tertiary flex items-center justify-center">
        <Loader loading={true} />
      </div>
    );
  }

  return (
    <div className="relative w-full h-full overflow-hidden bg-background-tertiary">
      {/* slides: only current is mounted initially; next mounts shortly before transition */}
      {[...mounted].map((i) => {
        const slide = slides[i];
        if (!slide) return undefined;

        const isCurrent = i === index;
        const isNext = i === nextIndex;
        return (
          <picture
            key={slide.unsplashId}
            className={`absolute inset-0 h-full w-full transition-opacity [transition-duration:1000ms] ${i % 2 === 0 ? 'kenburns-pan-left' : 'kenburns-pan-right'} ${isCurrent ? 'opacity-100' : 'opacity-0'}`}
            onTransitionEnd={(e) => handleTransitionEnd(i, e)}
          >
            {slide.src.avif ? <source type="image/avif" srcSet={slide.src.avif} /> : undefined}
            {slide.src.webp ? <source type="image/webp" srcSet={slide.src.webp} /> : undefined}
            <AppImage
              src={slide.src.jpeg}
              alt={`Photo of ${slide.location} by ${slide.photographer}`}
              className="absolute inset-0 h-full w-full object-cover"
              loading={isCurrent ? 'eager' : 'lazy'}
              decoding="async"
              fetchPriority={isCurrent ? 'high' : isNext ? 'low' : 'auto'}
            />
          </picture>
        );
      })}

      {/* caption / credits */}
      <div className="absolute bottom-0 left-0 right-0 z-20 p-4 text-xs text-white/80 text-right">
        <div className="inline-flex max-w-full items-center gap-1 rounded bg-black/60 px-2 py-1 backdrop-blur">
          <span>{currentSlide.location} by</span>
          <a
            href={`https://unsplash.com/photos/${currentSlide.unsplashId}`}
            target="_blank"
            rel="noreferrer noopener"
            className="underline hover:opacity-80"
          >
            {currentSlide.photographer}
          </a>
        </div>
      </div>
    </div>
  );
}

export default LoginSlideshow;
